import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "../components/ui/input";
import { MagnifyingGlass, Buildings, ChartLineUp, Users, ArrowRight, SealCheck, Trophy } from "@phosphor-icons/react";
import { getStats, seedData, getFeaturedColleges, getSiteConfig, ASSET_BASE } from "../lib/api";
import { IndiaCoverageMap } from "../components/IndiaCoverageMap";
import { MascotTour } from "../components/MascotTour";
import { MegaMenu } from "../components/MegaMenu";
import { HighlightedText } from "../components/HighlightedText";
import { getHighlight } from "../lib/highlightConfig";
import { getTextureCard } from "../lib/textures";
import { COURSE_MEGA_MENUS } from "../lib/megaMenuConfig";
import { CounsellingBoard } from "../components/CounsellingBoard";
import { CentralNewsroom } from "../components/CentralNewsroom";
import { CollegeStatsRail } from "../components/CollegeStatsRail";
import { FreshCollegesRail } from "../components/FreshCollegesRail";
import { NewsChannel } from "../components/NewsChannel";
import { GoogleReviews } from "../components/GoogleReviews";

export default function LandingPage() {
  const navigate = useNavigate();
  const [rank, setRank] = useState("");
  const [stats, setStats] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [featured, setFeatured] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [heroHighlight, setHeroHighlight] = useState(null);

  useEffect(() => {
    loadStats();
    getFeaturedColleges().then((res) => setFeatured(res.data.colleges || [])).catch(() => {});
    getSiteConfig().then((res) => setHeroHighlight(res.data.page_highlights || {})).catch(() => {});
  }, []);

  // Anchor-scroll after data-load. Landing sections mount asynchronously
  // (news, live board, fresh colleges rail) — a naive browser #hash scroll
  // fires before those sections exist, leaving the user at the top. This
  // effect polls for the target and scrolls once it appears.
  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (!hash) return;
    let tries = 0;
    const timer = setInterval(() => {
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        clearInterval(timer);
      } else if (++tries > 40) {   // give up after ~8s of polling
        clearInterval(timer);
      }
    }, 200);
    return () => clearInterval(timer);
  }, []);

  // Rotate the banner carousel every 5s
  useEffect(() => {
    if (featured.length < 2) return;
    const t = setInterval(() => setActiveIdx((i) => (i + 1) % featured.length), 5000);
    return () => clearInterval(t);
  }, [featured]);

  const activeCollege = featured[activeIdx] || null;

  const getStepTexture = (targetId) => {
    const h = getHighlight(heroHighlight, "home", targetId);
    return h ? getTextureCard(h.texture_id) : null;
  };

  const loadStats = async () => {
    try {
      const res = await getStats();
      setStats(res.data);
      if (res.data.total_colleges === 0) {
        setSeeding(true);
        await seedData();
        const res2 = await getStats();
        setStats(res2.data);
        setSeeding(false);
      }
    } catch (e) {
      console.error("Failed to load stats", e);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (rank) navigate(`/ug-neet/predict?rank=${rank}`);
  };

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Hero */}
      <section className="relative border-b border-zinc-200 overflow-hidden" data-testid="hero-section">
        {/* Rotating banner carousel of INI / top-NIRF colleges (wide photos) */}
        {featured.length > 0 ? (
          featured.map((c, i) => (
            <div
              key={c.college_code}
              className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000"
              style={{
                backgroundImage: `url(${ASSET_BASE}/colleges/${c.college_code}/header${c.header_version ? `?v=${c.header_version}` : ""})`,
                opacity: i === activeIdx ? 1 : 0,
                filter: "contrast(1.08) saturate(1.12)",
              }}
              aria-hidden="true"
            />
          ))
        ) : (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(https://images.unsplash.com/photo-1769284050048-168eda1bf9e1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxicnV0YWxpc3QlMjBhcmNoaXRlY3R1cmUlMjB1bml2ZXJzaXR5fGVufDB8fHx8MTc3NTgyNzcwNXww&ixlib=rb-4.1.0&q=85)` }}
          />
        )}
        {/* Clickable caption + dots are rendered below inside the content column */}
        <div className="absolute inset-0 bg-gradient-to-r from-white/70 via-white/35 to-transparent pointer-events-none" />
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <div className="absolute -top-10 left-[8%] w-72 h-72 rounded-full bg-[#FBBF24] blur-3xl mix-blend-multiply" style={{ animation: "blobFloat 9s ease-in-out infinite" }} />
          <div className="absolute top-1/3 left-[28%] w-80 h-80 rounded-full bg-[#C4B5FD] blur-3xl mix-blend-multiply" style={{ animation: "blobFloat 11s ease-in-out infinite reverse", animationDelay: "1.5s" }} />
          <div className="absolute -bottom-16 left-[2%] w-72 h-72 rounded-full bg-[#FDA4AF] blur-3xl mix-blend-multiply" style={{ animation: "blobFloat 10s ease-in-out infinite", animationDelay: "0.8s" }} />
          <div className="absolute top-8 right-[34%] w-64 h-64 rounded-full bg-[#5EEAD4] blur-3xl mix-blend-multiply" style={{ animation: "blobFloat 13s ease-in-out infinite reverse", animationDelay: "2.2s" }} />
          <div className="absolute bottom-4 left-[40%] w-72 h-72 rounded-full bg-[#FCD34D] blur-3xl mix-blend-multiply" style={{ animation: "blobFloat 8s ease-in-out infinite", animationDelay: "0.4s" }} />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-[120px] py-12 sm:py-20 lg:py-28">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-4 animate-fade-in">
              <span className="box-decoration-clone bg-white/70 text-[#52525B] px-1.5 py-0.5 rounded leading-[1.9]">NEET 2024-{new Date().getFullYear()} COUNSELLING PREDICTOR</span>
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-tight font-heading mb-6 animate-fade-in stagger-1">
              <span className="box-decoration-clone bg-white/70 text-[#09090B] px-2 py-0.5 rounded">Find Your Medical<br />College Match</span>
            </h1>
            <HighlightedText
              pageHighlights={heroHighlight}
              page="home"
              target="hero_intro"
              text="Enter your NEET All India Rank to discover colleges where you have the best chances of admission based on historical counselling data."
              as="div"
              className="text-base font-sans mb-8 max-w-xl leading-[2] animate-fade-in stagger-2"
              fallback={
                <p className="text-base font-sans mb-8 max-w-xl animate-fade-in stagger-2">
                  <span className="box-decoration-clone bg-white/75 text-[#52525B] px-2 py-[0.5em] rounded leading-[2]">Enter your NEET All India Rank to discover colleges where you have the best chances of admission based on historical counselling data.</span>
                </p>
              }
            />
            {stats?.total_colleges > 0 && false && (
              <div className="mb-6 -mt-3" data-testid="hero-coverage-line">
                <HighlightedText
                  pageHighlights={heroHighlight}
                  page="home"
                  target="coverage_line"
                  text={`${stats.total_colleges.toLocaleString()} colleges mapped across ${stats.total_states} states & UTs`}
                  as="div"
                  className="inline-block text-xs font-bold uppercase tracking-[0.18em] leading-[2] font-['IBM_Plex_Mono']"
                  fallback={
                    <p className="text-xs font-bold uppercase tracking-[0.18em] font-['IBM_Plex_Mono'] animate-fade-in stagger-2">
                      <span className="box-decoration-clone bg-white/75 text-[#6043EF] px-2 py-[0.5em] rounded leading-[2]">{stats.total_colleges.toLocaleString()} colleges mapped across {stats.total_states} states &amp; UTs</span>
                    </p>
                  }
                />
              </div>
            )}

            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-2 sm:gap-0 max-w-md animate-fade-in stagger-3" data-testid="hero-search-form">
              <div className="relative flex-1">
                <MagnifyingGlass
                  size={18}
                  weight="bold"
                  className="absolute left-3 top-1/2 text-[#6043EF] z-10"
                  style={{ animation: "iconPulse 1.4s ease-in-out infinite", transformOrigin: "center", translate: "0 -50%" }}
                />
                <Input
                  type="number"
                  placeholder="Enter your NEET AIR (e.g. 5000)"
                  value={rank}
                  onChange={(e) => setRank(e.target.value)}
                  className="pl-10 h-12 bg-white border-zinc-300 rounded-none shadow-[0_8px_24px_-6px_rgba(96,67,239,0.25)] focus:border-[#6043EF] focus:ring-[#6043EF] font-['IBM_Plex_Mono'] text-base"
                  data-testid="hero-rank-input"
                  min={1}
                />
              </div>
              <button
                type="submit"
                className="h-12 px-6 bg-[#6043EF] text-white font-bold text-sm uppercase tracking-wider hover:bg-[#4A28C2] transition-colors duration-150 flex items-center gap-2"
                data-testid="hero-search-btn"
              >
                PREDICT <ArrowRight size={14} weight="bold" />
              </button>
            </form>

            {/* Mobile-only featured caption — sits below the predict button (no dots) */}
            {activeCollege && (
              <div className="md:hidden mt-6 animate-fade-in stagger-3" data-testid="hero-featured-mobile">
                <button
                  type="button"
                  onClick={() => navigate(`/colleges/${activeCollege.seo_path || activeCollege.college_code}`)}
                  className="group inline-flex items-center gap-3 bg-white/95 border border-zinc-200 rounded-full pl-3 pr-4 py-2 shadow-soft backdrop-blur-sm hover:border-[#6043EF] transition-colors duration-150 max-w-full"
                  data-testid="hero-banner-link-mobile"
                  aria-label={`View ${activeCollege.name}`}
                >
                  {activeCollege.national_importance ? (
                    <span className="inline-flex items-center gap-1 bg-[#6043EF] text-white text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full shrink-0">
                      <SealCheck size={10} weight="fill" /> INI
                    </span>
                  ) : activeCollege.nirf_rank ? (
                    <span className="inline-flex items-center gap-1 border border-[#92700C] text-[#92700C] text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full shrink-0">
                      <Trophy size={10} weight="fill" /> NIRF #{activeCollege.nirf_rank}
                    </span>
                  ) : null}
                  <span className="text-left min-w-0">
                    <span className="block text-sm font-bold text-[#09090B] truncate max-w-[220px] group-hover:text-[#6043EF] transition-colors">{activeCollege.name}</span>
                    <span className="block text-[11px] text-[#52525B] font-sans truncate max-w-[220px]">{activeCollege.city}{activeCollege.state ? `, ${activeCollege.state}` : ""}</span>
                  </span>
                  <ArrowRight size={16} weight="bold" className="text-[#6043EF] shrink-0 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Featured college caption — top-right of banner (near map), desktop only */}
        {activeCollege && (
          <div className="hidden md:flex absolute top-4 right-4 sm:right-6 z-20 flex-col items-end gap-2" data-testid="hero-featured">
            <button
              type="button"
              onClick={() => navigate(`/colleges/${activeCollege.seo_path || activeCollege.college_code}`)}
              className="group inline-flex items-center gap-3 bg-white/95 border border-zinc-200 rounded-full pl-3 pr-4 py-2 shadow-soft backdrop-blur-sm hover:border-[#6043EF] transition-colors duration-150"
              data-testid="hero-banner-link"
              aria-label={`View ${activeCollege.name}`}
            >
              {activeCollege.national_importance ? (
                <span className="inline-flex items-center gap-1 bg-[#6043EF] text-white text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full shrink-0">
                  <SealCheck size={10} weight="fill" /> INI
                </span>
              ) : activeCollege.nirf_rank ? (
                <span className="inline-flex items-center gap-1 border border-[#92700C] text-[#92700C] text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full shrink-0">
                  <Trophy size={10} weight="fill" /> NIRF #{activeCollege.nirf_rank}
                </span>
              ) : null}
              <span className="text-left min-w-0">
                <span className="block text-sm font-bold text-[#09090B] truncate max-w-[240px] group-hover:text-[#6043EF] transition-colors">{activeCollege.name}</span>
                <span className="block text-[11px] text-[#52525B] font-sans truncate max-w-[240px]">{activeCollege.city}{activeCollege.state ? `, ${activeCollege.state}` : ""}</span>
              </span>
              <ArrowRight size={16} weight="bold" className="text-[#6043EF] shrink-0 group-hover:translate-x-1 transition-transform" />
            </button>
            {featured.length > 1 && (
              <div className="flex items-center gap-1.5" data-testid="hero-carousel-dots">
                {featured.map((c, i) => (
                  <button
                    key={c.college_code}
                    type="button"
                    onClick={() => setActiveIdx(i)}
                    className={`h-1.5 rounded-full transition-all duration-300 ${i === activeIdx ? "w-6 bg-[#6043EF]" : "w-1.5 bg-white/80 hover:bg-white shadow"}`}
                    aria-label={`Show ${c.name}`}
                    data-testid={`hero-dot-${i}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Coverage map — right half overlay (lg+), on top so dots are hoverable */}
        <div className="hidden lg:block absolute right-0 top-0 bottom-0 w-1/2 pointer-events-none" data-testid="hero-map-overlay">
          <div className="absolute inset-0 flex items-center justify-center pr-6 xl:pr-16">
            <div className="relative w-[420px] h-[520px] pointer-events-auto">
              <IndiaCoverageMap focusCode={activeCollege?.college_code} />
              <div className="absolute bottom-2 left-2 flex items-center gap-2 bg-white/95 border border-zinc-200 rounded-full pl-2.5 pr-3.5 py-1.5 shadow-soft backdrop-blur-sm">
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#F59E0B] ring-2 ring-[#FDE047] shrink-0" />
                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#09090B] font-['IBM_Plex_Mono'] whitespace-nowrap">
                  Institutes of National Importance
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Grid */}
      {stats && (
        <section className="border-b border-zinc-200" data-testid="stats-section">
          <div className="max-w-[1400px] mx-auto grid grid-cols-2 lg:grid-cols-4 lg:px-[120px]">
            <StatCard
              label="TOTAL COLLEGES"
              value={stats.total_colleges}
              icon={Buildings}
              testId="stat-colleges"
              delay="stagger-1"
            />
            <StatCard
              label="TOTAL SEATS"
              value={stats.total_seats?.toLocaleString()}
              icon={Users}
              testId="stat-seats"
              delay="stagger-2"
            />
            <StatCard
              label="STATES COVERED"
              value={stats.total_states}
              icon={ChartLineUp}
              testId="stat-states"
              delay="stagger-3"
            />
            <StatCard
              label="ALLOTMENT RECORDS"
              value={stats.total_raw_allotments?.toLocaleString()}
              icon={ChartLineUp}
              testId="stat-records"
              delay="stagger-4"
              noBorder
            />
          </div>
        </section>
      )}

      {/* Counselling flight board (airport-style dashboard) — homepage-only font boost */}
      <div id="live-board" className="home-section-boost">
        <CounsellingBoard />
      </div>

      {/* College Stats Rail — 2 informational cards below Live Board */}
      <CollegeStatsRail />

      {/* Fresh Colleges Rail — property-listing style cards for the ~25 newly
          approved AY 2026-27 MBBS colleges (auto-hides when empty). */}
      <FreshCollegesRail />

      {/* Central Newsroom — homepage-only font boost */}
      <div id="central-newsroom" className="home-section-boost">
        <CentralNewsroom />
      </div>

      {/* How It Works */}
      <section className="border-b border-zinc-200 py-20 bg-gradient-to-b from-white to-[#F7F5FF]" data-testid="how-it-works">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-[120px]">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6043EF] mb-2">HOW IT WORKS</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-none text-[#09090B] font-heading mb-4">
            Predict your college in 3 steps
          </h2>

          <MascotTour
            steps={[
              { step: "01", title: "Enter Your Rank", desc: "Provide your NEET All India Rank, category, and domicile state.", tone: "from-[#EEF2FF] to-[#E0E7FF]", texture: getStepTexture("step_rank") },
              { step: "02", title: "Apply Filters", desc: "Set preferences like state, college type, and fees budget to narrow results.", tone: "from-[#F5F3FF] to-[#EDE9FE]", texture: getStepTexture("step_filters") },
              { step: "03", title: "View Predictions", desc: "Get a ranked list of colleges with chance indicators based on historical data.", tone: "from-[#FEFCE8] to-[#FEF9C3]", texture: getStepTexture("step_predictions") },
            ]}
          />
        </div>
      </section>

      {/* News Channel — cross-college live news, moved here from /colleges */}
      <section id="colleges-news" className="border-b border-zinc-200 py-10 home-section-boost" data-testid="home-news-channel">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-[120px]">
          <NewsChannel />
        </div>
      </section>

      {/* CTA */}
      <section className="py-16" data-testid="cta-section">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-[120px] flex flex-col lg:flex-row items-start lg:items-center gap-6 lg:gap-10">
          <div className="flex-1">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight leading-none text-[#09090B] font-heading mb-4">
              Data-driven decisions for your medical career
            </h2>
            <HighlightedText
              pageHighlights={heroHighlight}
              page="home"
              target="cta_paragraph"
              text="Our platform aggregates counselling data from MCC and state counselling bodies to provide accurate predictions for NEET candidates across all categories."
              as="div"
              className="text-base font-sans leading-[2] text-[#52525B] mb-6 max-w-lg"
              fallback={
                <p className="text-base font-sans leading-relaxed text-[#52525B] mb-6 max-w-lg">
                  Our platform aggregates counselling data from MCC and state counselling bodies to provide accurate predictions for NEET candidates across all categories.
                </p>
              }
            />
            <button
              onClick={() => navigate("/ug-neet/predict")}
              className="h-12 px-8 bg-[#6043EF] text-white font-bold text-sm uppercase tracking-wider hover:bg-[#4A28C2] transition-colors duration-150 flex items-center gap-2"
              data-testid="cta-predict-btn"
            >
              START PREDICTING <ArrowRight size={14} weight="bold" />
            </button>
          </div>
          <div className="hidden lg:block w-[400px] h-[260px] overflow-hidden border border-zinc-200">
            <img
              src="https://images.unsplash.com/photo-1758691463333-c79215e8bc3b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxzdGV0aG9zY29wZSUyMGRvY3RvciUyMGRlc2t8ZW58MHx8fHwxNzc1ODI3NzEyfDA&ixlib=rb-4.1.0&q=85"
              alt="Medical career"
              className="w-full h-full object-cover grayscale contrast-125"
            />
          </div>
        </div>
      </section>

      <GoogleReviews />

      {seeding && (
        <div className="fixed bottom-4 right-4 bg-[#09090B] text-white px-4 py-2 text-xs font-['IBM_Plex_Mono'] z-50" data-testid="seeding-indicator">
          Seeding demo data...
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, testId, delay, noBorder }) {
  return (
    <div
      className={`p-6 lg:p-8 ${!noBorder ? "border-r border-zinc-200" : ""} animate-fade-in ${delay}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} weight="bold" className="text-[#6043EF]" />
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B]">{label}</span>
      </div>
      <span className="text-3xl lg:text-4xl font-black font-['IBM_Plex_Mono'] text-[#09090B]">{value || 0}</span>
    </div>
  );
}
